import { yupResolver } from '@hookform/resolvers/yup';
import IBAN from 'iban';
import moment from 'moment';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { EDevice } from '../../../../redux/sequence/enums';
import { RootState } from '../../../../redux/types';
import { PrivateCustomer } from '../../../../types/instant-lease-api';
import Button from '../../../b2b/components/button/button';
import Checkbox from '../../../b2b/components/checkbox/checkbox';
import InternationalPhoneInput from '../../../b2b/components/international-phone-input/international-phone-input';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import FormLayout from '../../../common/form-layout/form-layout';
import InputDate from '../../../common/input-field/input-date';
import InputField from '../../../common/input-field/input-field';

interface WorkContactBankTemplateProps {
  onSubmit: (data: WorkContactBankData) => void;
  tabButton: boolean;
  privateCustomer: PrivateCustomer | undefined;
  loading?: boolean;
}

export interface WorkContactBankData {
  employment_since: string;
  email: string;
  phone_number: string;
  terms: NonNullable<boolean | undefined>;
  account_owner: string;
  iban: string;
}

const WorkContactBankTemplate: React.FC<WorkContactBankTemplateProps> = ({
  privateCustomer,
  onSubmit,
  tabButton,
  loading,
}) => {
  // sequence Storage
  const { device } = useSelector((state: RootState) => state.sequence);

  // Set country code when user pick flag
  const [countryCode, setCountry] = useState<string>('+49');

  const contacts = privateCustomer?.person?.contact_details;

  // Validation Schema
  const validationSchema = yup.object().shape({
    employment_since: yup
      .string()
      .required('Pflichtfeld')
      .matches(
        /^(((0?[1-9]|[12]\d|3[01])\.(0[13578]|[13578]|1[02])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|[12]\d|30)\.(0[13456789]|[13456789]|1[012])\.((1[6-9]|[2-9]\d)\d{2}))|((0?[1-9]|1\d|2[0-8])\.0?2\.((1[6-9]|[2-9]\d)\d{2}))|(29\.0?2\.((1[6-9]|[2-9]\d)(0[48]|[2468][048]|[13579][26])|((16|[2468][048]|[3579][26])00))))$/,
        'Bitte Datum eintragen (TT.MM.JJJJ)',
      )
      .test(
        'maxDateToday',
        'Das Datum darf nicht in der Zukunft liegen',
        (value) => {
          return !value || moment(value, 'DD.MM.YYYY') <= moment();
        },
      ),
    email: yup
      .string()
      .email('Bitte vollständige Email Adresse eintragen')
      .required('Pflichtfeld'),
    phone_number: yup
      .string()
      .matches(
        /^[1-9][0-9]+$/gm,
        'Bitte geben Sie eine gültige Telefonnummer ohne führende 0 an, z.B.: 123456789',
      )
      .required('Pflichtfeld'),
    terms: yup
      .boolean()
      .required('Bitte akzeptiere die Bedingungen um fortzufahren')
      .oneOf([true], 'Bitte akzeptiere die Bedingungen um fortzufahren'),
    account_owner: yup.string().required('Pflichtfeld'),
    iban: yup
      .string()
      .required('Pflichtfeld')
      .max(34)
      .test(
        'test-iban',
        'diese IBAN ist unbekannt. Bitte prüfen.',
        (iban: any) => {
          return iban && IBAN.isValid(iban) && iban.match(/DE|de+/g);
        },
      ),
  });

  // useForm hook
  const {
    register,
    control,
    formState: { errors },
    trigger,
    setValue,
    handleSubmit,
  } = useForm<WorkContactBankData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      employment_since: privateCustomer?.living_details?.employment_since,
      email:
        (contacts?.emails &&
          contacts?.emails?.length > 0 &&
          contacts?.emails[0].value) ||
        '',
      phone_number:
        (contacts?.phone_numbers &&
          contacts?.phone_numbers.length &&
          contacts?.phone_numbers[0].value) ||
        '',
      terms:
        (contacts?.phone_numbers &&
          contacts?.phone_numbers.length &&
          contacts?.phone_numbers[0].data_usage?.answer) ||
        false,
      account_owner: privateCustomer?.bank_account?.account_owner
        ? privateCustomer?.bank_account?.account_owner
        : `${privateCustomer?.person?.first_name} ${privateCustomer?.person?.last_name}`,
      iban: privateCustomer?.bank_account?.iban,
    },
  });

  // Submit Data
  const submitData = (data: WorkContactBankData) => {
    onSubmit({
      ...data,
      phone_number: `${countryCode}${data.phone_number}`,
    });
  };

  return (
    <form onSubmit={handleSubmit(submitData)}>
      <FormLayout sectionTitle='Beschäftigung'>
        <InputDate
          label='Beschäftigt seit?'
          trigger={trigger}
          {...register('employment_since')}
          setValue={setValue}
          placeholder='TT.MM.JJJJ'
          error={errors.employment_since?.message}
        />
      </FormLayout>

      <FormLayout sectionTitle='Kontaktdaten'>
        <InputField
          label='E-Mail-Adresse'
          {...register('email')}
          error={errors.email?.message}
        />

        <InternationalPhoneInput
          label='Mobiltelefonnummer'
          getCode={(value) => setCountry(value)}
          code={countryCode}
          placeholder='123456789'
          {...register('phone_number')}
          error={errors?.phone_number?.message}
        />

        <div className='input-margin'>
          <div></div>
          <Checkbox
            square
            checkMarkType='check'
            checkboxPosition='top'
            type='checkbox'
            {...register('terms')}
            errorMessage={errors.terms?.message}
          >
            <p className='small-text dark-grey'>
              Ich stimme zu, dass meine Telefonnummer und E-Mail-Adresse durch
              die ALD AutoLeasing D GmbH zum Zwecke der schnellen und
              reibungslosen Bestellung für 90 Tage gespeichert werden darf. Ich
              bin einverstanden, dass mich die ALD zum Zwecke des
              Bestellabschlusses kontaktieren darf, wenn ich den Bestellvorgang
              nicht vollständig durchgeführt habe. Hinweis: Du kannst diese
              Einwilligung jederzeit mit Wirkung für die Zukunft per E-Mail an{' '}
              <a
                href='mailto:datenschutz-widerruf@aldautomotive.com'
                target='_blank'
                rel='noopener noreferrer'
              >
                datenschutz-widerruf@aldautomotive.com
              </a>{' '}
              widerrufen.
            </p>
          </Checkbox>
        </div>
      </FormLayout>

      <FormLayout sectionTitle='Bankverbindung'>
        <InputField
          label='Name des Kontoinhabers'
          readOnly
          {...register('account_owner')}
          error={errors.account_owner?.message}
        />
        <Controller
          name='iban'
          control={control}
          render={({ field }) => (
            <InputField
              label='IBAN'
              type='iban'
              error={errors.iban?.message}
              {...field}
            />
          )}
        />
        {tabButton ? (
          <>
            {device === EDevice.MOBILE ? (
              <ButtonGroup
                className='back-next medium'
                type='back-next'
                data-testid='disabled-forward-button'
                appearOnFront
                buttonOneProps={{
                  type: 'button',
                  name: 'form-submit',
                  dataTestId: 'next',
                  children: 'nächster Schritt',
                  loading,
                  onClick: handleSubmit(onSubmit),
                }}
              />
            ) : (
              <div className='add-additional-person'>
                <Button
                  theme='secondary'
                  type='button'
                  className='save-button next button-text save-and-next-tab'
                  role='presentation'
                  loading={loading}
                  onClick={handleSubmit(onSubmit)}
                >
                  nächster Schritt
                </Button>
              </div>
            )}
          </>
        ) : (
          <ButtonGroup
            type='back-next'
            className='back-next'
            buttonOneProps={{
              type: 'submit',
              name: 'form-submit',
              loading,
              dataTestId: 'start-fetching',
              children: 'weiter',
            }}
          />
        )}
      </FormLayout>
    </form>
  );
};

export default WorkContactBankTemplate;
